// Spreadsheet import of past expenses. Pure helpers, tested in financeImport.test.js.
// The browser only reads the file and lines its columns up with the expense fields; every value is interpreted
// by the server (POST /finance/imports/preview), with the same rules as the expense form.
import { decodeFile, parseCsv } from "./collaboratorImport";

export { decodeFile, parseCsv };
export const MAX_ROWS = 500;

export const IMPORT_FIELDS = [
  { key: "date", label: "Date", hint: "JJ/MM/AAAA. Date de la dépense, et du paiement si « payé par » est rempli." },
  { key: "title", label: "Libellé", required: true },
  { key: "amount", label: "Montant", required: true, hint: "Ex. 12,50 ou 12 500." },
  { key: "currency", label: "Devise", hint: "EUR, XOF… Vide : la devise par défaut." },
  { key: "category", label: "Catégorie", hint: "Nom d'une catégorie existante." },
  { key: "supplier", label: "Fournisseur" },
  { key: "paid_by", label: "Payé par", hint: "Nom ou référence d'un collaborateur, « Jamora », ou un tiers." },
  { key: "invoice_reference", label: "Référence facture" },
  { key: "project", label: "Projet" },
  { key: "description", label: "Description" },
  { key: "preconstitution", label: "Avant constitution", hint: "oui / non." },
];
export const FIELD_LABELS = Object.fromEntries(IMPORT_FIELDS.map((f) => [f.key, f.label]));

const fold = (value) => (value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

// Header synonyms (folded): what people actually write in their sheets.
const HEADER_ALIASES = {
  date: ["date", "date de paiement", "date paiement", "date depense", "date de la depense", "jour"],
  title: ["libelle", "intitule", "objet", "depense", "designation", "nature", "titre", "label"],
  amount: ["montant", "montant total", "prix", "cout", "total", "somme", "montant ttc", "ttc"],
  currency: ["devise", "monnaie", "currency"],
  category: ["categorie", "type", "poste", "rubrique", "category"],
  supplier: ["fournisseur", "vendeur", "marchand", "prestataire", "chez", "supplier", "boutique", "magasin"],
  paid_by: ["paye par", "payeur", "qui a paye", "payee par", "paid by", "regle par", "avance par"],
  invoice_reference: ["reference", "reference facture", "ref", "ref facture", "numero facture", "n facture", "facture"],
  project: ["projet", "project", "centre de cout"],
  description: ["description", "commentaire", "commentaires", "notes", "note", "details", "remarque"],
  preconstitution: ["avant constitution", "preconstitution", "pre constitution", "avant creation"],
};

// Header row → { field: columnIndex }; each field takes the first matching column, extra columns are ignored.
export function guessMapping(headerRow) {
  const mapping = {};
  (headerRow || []).forEach((raw, index) => {
    const key = fold(raw);
    const field = Object.keys(HEADER_ALIASES).find((f) => HEADER_ALIASES[f].includes(key));
    if (field && mapping[field] === undefined) mapping[field] = index;
  });
  return mapping;
}

// What still prevents checking the file with this mapping.
export function mappingProblems(mapping) {
  return IMPORT_FIELDS.filter((f) => f.required && mapping[f.key] === undefined).map((f) => `la colonne « ${f.label} »`);
}

// Data rows → request rows (every cell as typed; `line` is the spreadsheet line number, header = line 1).
export function buildRows(table, mapping) {
  return table.slice(1).map((cells, i) => {
    const row = { line: i + 2 };
    for (const [field, index] of Object.entries(mapping)) {
      const value = (cells[index] ?? "").trim();
      if (value) row[field] = value;
    }
    return row;
  });
}

// Changing a column choice: a field lives in one column only.
export function assignColumn(mapping, columnIndex, field) {
  const next = Object.fromEntries(Object.entries(mapping).filter(([f, i]) => i !== columnIndex && f !== field));
  if (field) next[field] = columnIndex;
  return next;
}

export const fieldForColumn = (mapping, columnIndex) => Object.keys(mapping).find((f) => mapping[f] === columnIndex) || null;

export function templateCsv() {
  const header = ["Date", "Libellé", "Montant", "Devise", "Catégorie", "Fournisseur", "Payé par", "Référence facture", "Projet", "Description", "Avant constitution"];
  const example = ["05/10/2026", "Abonnement Figma", "12,50", "EUR", "Logiciels", "Figma", "Awa Ndiaye", "", "", "", "non"];
  const quote = (v) => (/[;"\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  return "﻿" + [header, example].map((r) => r.map(quote).join(";")).join("\r\n") + "\r\n";
}
